import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Error as ErrorIcon,
  Check as CheckIcon
} from '@mui/icons-material'
import {Collapse, ListItemButton, ListItemIcon, ListItemText, IconButton, Box} from '@mui/material'
import {useState} from 'react'

import {VerticalNavigationItemStatus} from '../../../common/types'
import {VerticalNavigationTreeNode} from '../../../common/types/verticalNavigation.types'
import {dataTestId} from '../../../common/utils'

const StatusIcon = ({status}: {status: VerticalNavigationItemStatus}) => {
  const isCompleted = status === VerticalNavigationItemStatus.Ok
  const isError = status === VerticalNavigationItemStatus.Error
  return (
    <Box width="24px" display="flex" justifyContent="center" alignItems="center">
      {isError && <ErrorIcon color="error" {...dataTestId('error-icon')} />}
      {!isError && isCompleted && <CheckIcon color="success" {...dataTestId('check-icon')} />}
    </Box>
  )
}

const ExpandCollapseIcon = ({
  expanded,
  setExpanded
}: {
  expanded: boolean
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>
}) => (
  <ListItemIcon
    sx={{
      justifyContent: 'center',
      alignItems: 'center',
      height: '40px',
      width: '40px'
    }}
  >
    <IconButton
      size="medium"
      onClick={(e) => {
        setExpanded((prev) => !prev)
        e.stopPropagation()
      }}
    >
      {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </IconButton>
  </ListItemIcon>
)

export const VerticalNavigationItem = ({
  node,
  level,
  onClick,
  selectedItemId,
  oldStructure
}: {
  node?: VerticalNavigationTreeNode
  level: number
  onClick: (item: string) => void
  selectedItemId?: string
  oldStructure?: boolean
}) => {
  const [expanded, setExpanded] = useState(true)

  if (!node) return null

  const isClickable = level !== 0 || !oldStructure

  return (
    <>
      <ListItemButton
        key={node.id}
        selected={node.id === selectedItemId}
        sx={{
          ml: level,
          // Theme-adaptive divider: dark in dark mode, light on light surfaces.
          borderBottom: `1px solid var(--border, #E8ECF0)`,
          cursor: isClickable ? 'pointer' : 'default'
        }}
        onClick={() => isClickable && onClick(node.id)}
        {...dataTestId(`vertical-navigation-item-${node.id}`)}
      >
        <ListItemText
          primary={
            <Box display="flex" flexDirection="row" alignContent="center" gap={1}>
              <StatusIcon status={node.status} />
              <Box
                sx={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '100%'
                }}
              >
                {node.name}
              </Box>
            </Box>
          }
          primaryTypographyProps={{variant: 'body1'}}
        />
        {node.children?.length && !node.hideGroups ? (
          <ExpandCollapseIcon expanded={expanded} setExpanded={setExpanded} />
        ) : null}
      </ListItemButton>

      <Collapse in={expanded} timeout="auto">
        {!node.hideGroups &&
          node.children?.map((item) => (
            <VerticalNavigationItem
              oldStructure={oldStructure}
              onClick={onClick}
              selectedItemId={selectedItemId}
              key={item.id}
              node={item}
              level={level + 2}
            />
          ))}
      </Collapse>
    </>
  )
}
